import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  FileSpreadsheet,
  Upload,
  LayoutTemplate,
  Send,
  Check,
  X,
  Loader2,
  Eye,
  AlertTriangle,
  History,
  Braces,
  FlaskConical,
  Bold,
  Italic,
  Underline,
  List,
  Link2,
  Undo2,
  RemoveFormatting,
  Lock,
  Pencil,
} from 'lucide-react';
import {
  useBroadcastStatus,
  useBroadcasts,
  useCreateBroadcast,
  useSendBroadcastBatch,
  useSendBroadcastTest,
  type BroadcastListItem,
} from '@/lib/queries';
import { EMAIL_TEMPLATES, buildEmailHtml } from '@/lib/emailTemplates';
import { PageTransition, FadeIn } from '@/components/ui/motion';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BATCH_SIZE = 25;
const VAR_RE = /\{\{\s*([A-Za-z0-9_ÁÉÍÓÚÑáéíóúñ_]+)\s*\}\}/g;

type Row = Record<string, string>;

interface ParsedExcel {
  fileName: string;
  columns: string[];
  rows: Row[];
  invalidCount: number;
  duplicateCount: number;
}

function escapeHtml(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Igual que el backend: sólo sustituye columnas del Excel; lo demás queda literal. */
function renderVars(tpl: string, row: Row, columns: string[], escape: boolean): string {
  const allowed = new Set(columns);
  return tpl.replace(VAR_RE, (full, key: string) => {
    const k = key.trim().toUpperCase();
    if (!allowed.has(k)) return full;
    const val = row[k] ?? '';
    return escape ? escapeHtml(val) : val;
  });
}

/** Variables usadas en un texto que NO existen como columnas del Excel. */
function unknownVars(text: string, columns: string[]): string[] {
  const allowed = new Set(columns);
  const found = new Set<string>();
  let m: RegExpExecArray | null;
  const re = new RegExp(VAR_RE.source, 'g');
  while ((m = re.exec(text))) {
    const k = m[1].trim().toUpperCase();
    if (!allowed.has(k)) found.add(k);
  }
  return [...found];
}

async function parseExcel(file: File): Promise<ParsedExcel> {
  const buf = await file.arrayBuffer();
  // codepage 65001 = UTF-8 (para CSV con acentos; los .xlsx no lo necesitan)
  const wb = XLSX.read(buf, { codepage: 65001 });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
  if (raw.length === 0) throw new Error('El archivo no tiene filas de datos.');

  const colSet = new Set<string>();
  const rows: Row[] = raw.map((r) => {
    const out: Row = {};
    for (const [k, v] of Object.entries(r)) {
      const key = String(k).trim().toUpperCase();
      if (!key || key.startsWith('__EMPTY')) continue;
      colSet.add(key);
      out[key] = v == null ? '' : String(v).trim();
    }
    return out;
  });
  const columns = [...colSet];
  if (!columns.includes('CORREO')) {
    throw new Error(
      'El Excel debe incluir la columna CORREO (es el destinatario). Columnas encontradas: ' +
        columns.join(', '),
    );
  }

  const seen = new Set<string>();
  const valid: Row[] = [];
  let invalidCount = 0;
  let duplicateCount = 0;
  for (const row of rows) {
    const email = (row['CORREO'] ?? '').toLowerCase();
    if (!EMAIL_RE.test(email)) {
      invalidCount++;
      continue;
    }
    if (seen.has(email)) {
      duplicateCount++;
      continue;
    }
    seen.add(email);
    valid.push({ ...row, CORREO: email });
  }
  if (valid.length === 0) throw new Error('Ninguna fila tiene un correo válido en la columna CORREO.');
  return { fileName: file.name, columns, rows: valid, invalidCount, duplicateCount };
}

const STEPS = [
  { n: 1, label: 'Destinatarios', icon: FileSpreadsheet },
  { n: 2, label: 'Plantilla', icon: LayoutTemplate },
  { n: 3, label: 'Contenido', icon: Pencil },
  { n: 4, label: 'Enviar', icon: Send },
];

export function BroadcastPage() {
  const { data: status } = useBroadcastStatus();
  const [step, setStep] = useState(1);
  const [excel, setExcel] = useState<ParsedExcel | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyInner, setBodyInner] = useState('');

  const reset = () => {
    setStep(1);
    setExcel(null);
    setParseError(null);
    setTemplateKey(null);
    setSubject('');
    setBodyInner('');
  };

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/colaboradores"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary"
          >
            <ArrowLeft size={16} />
          </Link>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <Mail size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Difusiones por correo</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Envía correos personalizados a los colaboradores desde{' '}
              <span className="font-semibold text-primary">{status?.sender ?? 'soporte@isoft-ste.com'}</span>
            </p>
          </div>
        </div>
      </div>

      {status && !status.configured && (
        <div className="flex items-center gap-2 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm font-medium text-warning">
          <AlertTriangle size={16} /> El servicio de correo no está configurado. Contacta a TI.
        </div>
      )}

      {/* Pasos */}
      <FadeIn delay={0.05}>
        <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-white p-1">
          {STEPS.map((s) => (
            <button
              key={s.n}
              onClick={() => s.n < step && setStep(s.n)}
              disabled={s.n > step}
              className={cn(
                'flex h-10 flex-1 items-center justify-center gap-2 rounded-md text-xs font-semibold transition sm:text-sm',
                step === s.n
                  ? 'bg-primary text-white shadow-sm'
                  : s.n < step
                    ? 'text-primary hover:bg-primary-subtle'
                    : 'cursor-not-allowed text-font-tertiary',
              )}
            >
              <s.icon size={15} />
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{s.n}</span>
              {s.n < step && <Check size={13} className="text-accent" />}
            </button>
          ))}
        </div>
      </FadeIn>

      {step === 1 && (
        <StepExcel
          excel={excel}
          parseError={parseError}
          onParsed={(p) => {
            setExcel(p);
            setParseError(null);
          }}
          onError={(e) => {
            setExcel(null);
            setParseError(e);
          }}
          onNext={() => setStep(2)}
        />
      )}
      {step === 2 && excel && (
        <StepTemplate
          columns={excel.columns}
          selected={templateKey}
          onSelect={(key) => {
            const tpl = EMAIL_TEMPLATES.find((t) => t.key === key)!;
            setTemplateKey(key);
            setSubject(tpl.subject);
            setBodyInner(tpl.inner);
            setStep(3);
          }}
        />
      )}
      {step === 3 && excel && templateKey && (
        <StepEditor
          key={templateKey}
          excel={excel}
          subject={subject}
          setSubject={setSubject}
          bodyInner={bodyInner}
          setBodyInner={setBodyInner}
          onNext={() => setStep(4)}
        />
      )}
      {step === 4 && excel && (
        <StepSend
          excel={excel}
          subject={subject}
          bodyInner={bodyInner}
          templateKey={templateKey}
          onDone={reset}
        />
      )}

      <BroadcastHistory />
    </PageTransition>
  );
}

/* ============================ Paso 1: Excel ============================ */

function StepExcel({
  excel,
  parseError,
  onParsed,
  onError,
  onNext,
}: {
  excel: ParsedExcel | null;
  parseError: string | null;
  onParsed: (p: ParsedExcel) => void;
  onError: (e: string) => void;
  onNext: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file: File) => {
    setBusy(true);
    try {
      onParsed(await parseExcel(file));
    } catch (e: any) {
      onError(e?.message ?? 'No se pudo leer el archivo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handleFile(f);
        }}
        onClick={() => inputRef.current?.click()}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed bg-white p-8 text-center transition sm:p-12',
          dragOver ? 'border-primary bg-primary-subtle' : 'border-border hover:border-primary/50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
        />
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-subtle">
          {busy ? (
            <Loader2 size={26} className="animate-spin text-primary" />
          ) : (
            <Upload size={26} className="text-primary" />
          )}
        </span>
        <div>
          <p className="text-sm font-bold text-font-primary">
            Arrastra tu Excel aquí o haz clic para buscarlo
          </p>
          <p className="mt-1 text-xs text-font-secondary">
            Formatos: .xlsx, .xls, .csv · La columna <strong>CORREO</strong> es obligatoria (es el
            destinatario). Las demás columnas se vuelven variables: NOMBRE, PASS, etc.
          </p>
        </div>
      </div>

      {parseError && (
        <div className="flex items-start gap-2 rounded-xl border border-error/30 bg-red-50 p-4 text-sm text-error">
          <X size={16} className="mt-0.5 flex-shrink-0" /> {parseError}
        </div>
      )}

      {excel && (
        <FadeIn>
          <div className="rounded-xl border border-border bg-white p-5">
            <div className="flex flex-wrap items-center gap-2">
              <FileSpreadsheet size={18} className="text-accent" />
              <span className="text-sm font-bold text-font-primary">{excel.fileName}</span>
              <Badge variant="success">{excel.rows.length} destinatarios válidos</Badge>
              {excel.invalidCount > 0 && (
                <Badge variant="warning">{excel.invalidCount} correos inválidos (excluidos)</Badge>
              )}
              {excel.duplicateCount > 0 && (
                <Badge variant="neutral">{excel.duplicateCount} duplicados (excluidos)</Badge>
              )}
            </div>

            <p className="mb-2 mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-font-tertiary">
              <Braces size={12} /> Variables disponibles
            </p>
            <div className="flex flex-wrap gap-1.5">
              {excel.columns.map((c) => (
                <span
                  key={c}
                  className={cn(
                    'rounded-full px-2.5 py-1 font-mono text-xs font-semibold',
                    c === 'CORREO' ? 'bg-primary text-white' : 'bg-primary-subtle text-primary',
                  )}
                >
                  {'{{'}{c}{'}}'}
                </span>
              ))}
            </div>

            <p className="mb-2 mt-4 text-xs font-bold uppercase tracking-wide text-font-tertiary">
              Vista previa (primeras 5 filas)
            </p>
            <div className="overflow-x-auto rounded-lg border border-border-light">
              <table className="w-full text-left text-xs">
                <thead className="bg-bg">
                  <tr>
                    {excel.columns.map((c) => (
                      <th key={c} className="whitespace-nowrap px-3 py-2 font-semibold text-font-secondary">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {excel.rows.slice(0, 5).map((row, i) => (
                    <tr key={i} className="border-t border-border-light">
                      {excel.columns.map((c) => (
                        <td key={c} className="max-w-[220px] truncate whitespace-nowrap px-3 py-2 text-font-primary">
                          {row[c] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={onNext}
              className="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark sm:w-auto sm:px-6"
            >
              Continuar a plantillas <ArrowRight size={15} />
            </button>
          </div>
        </FadeIn>
      )}
    </div>
  );
}

/* ============================ Paso 2: Plantilla ============================ */

function StepTemplate({
  columns,
  selected,
  onSelect,
}: {
  columns: string[];
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {EMAIL_TEMPLATES.map((tpl) => {
        const missing = tpl.suggestedVars.filter((v) => !columns.includes(v));
        return (
          <button
            key={tpl.key}
            onClick={() => onSelect(tpl.key)}
            className={cn(
              'flex flex-col rounded-xl border bg-white p-5 text-left transition hover:border-primary hover:shadow-md lift',
              selected === tpl.key ? 'border-primary ring-2 ring-primary/20' : 'border-border',
            )}
          >
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-subtle">
                <LayoutTemplate size={17} className="text-primary" />
              </span>
              <span className="text-sm font-bold text-font-primary">{tpl.name}</span>
            </div>
            <p className="mt-2 flex-1 text-xs text-font-secondary">{tpl.description}</p>
            <div className="mt-3 flex flex-wrap gap-1">
              {tpl.suggestedVars.map((v) => (
                <span
                  key={v}
                  className={cn(
                    'rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold',
                    columns.includes(v) ? 'bg-accent-light text-accent' : 'bg-warning/10 text-warning',
                  )}
                >
                  {'{{'}{v}{'}}'}
                </span>
              ))}
            </div>
            {missing.length > 0 && (
              <p className="mt-2 flex items-center gap-1 text-[11px] text-warning">
                <AlertTriangle size={11} /> Tu Excel no trae: {missing.join(', ')}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ============================ Paso 3: Editor visual ============================ */

function StepEditor({
  excel,
  subject,
  setSubject,
  bodyInner,
  setBodyInner,
  onNext,
}: {
  excel: ParsedExcel;
  subject: string;
  setSubject: (s: string) => void;
  bodyInner: string;
  setBodyInner: (s: string) => void;
  onNext: () => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const savedRange = useRef<Range | null>(null);
  // El contenido inicial queda congelado: el div es no-controlado (el cursor no salta)
  const initialInner = useRef(bodyInner);
  const [lastFocus, setLastFocus] = useState<'subject' | 'editor'>('editor');
  const [mode, setMode] = useState<'editar' | 'preview'>('editar');
  const [previewRow, setPreviewRow] = useState(0);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const sendTest = useSendBroadcastTest();

  const syncFromEditor = () => {
    if (editorRef.current) setBodyInner(editorRef.current.innerHTML);
  };

  /** Guarda la posición del cursor dentro del editor (para insertar variables). */
  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      savedRange.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (cmd: string, val?: string) => {
    restoreSelection();
    document.execCommand(cmd, false, val);
    saveSelection();
    syncFromEditor();
  };

  const insertVar = (col: string) => {
    const token = `{{${col}}}`;
    if (lastFocus === 'subject' && subjectRef.current) {
      const el = subjectRef.current;
      const pos = el.selectionStart ?? subject.length;
      setSubject(subject.slice(0, pos) + token + subject.slice(pos));
    } else {
      restoreSelection();
      document.execCommand('insertText', false, token);
      saveSelection();
      syncFromEditor();
    }
  };

  const unknown = useMemo(
    () => unknownVars(subject + ' ' + bodyInner, excel.columns),
    [subject, bodyInner, excel.columns],
  );

  const row = excel.rows[previewRow] ?? excel.rows[0];
  const previewHtml = useMemo(
    () => buildEmailHtml(renderVars(bodyInner, row, excel.columns, true)),
    [bodyInner, row, excel.columns],
  );

  const canContinue = subject.trim().length > 0 && bodyInner.trim().length > 0 && unknown.length === 0;

  const TOOLS = [
    { icon: Bold, cmd: 'bold', title: 'Negrita' },
    { icon: Italic, cmd: 'italic', title: 'Cursiva' },
    { icon: Underline, cmd: 'underline', title: 'Subrayado' },
    { icon: List, cmd: 'insertUnorderedList', title: 'Lista' },
  ] as const;

  return (
    <div className="flex flex-col gap-4">
      {/* Variables */}
      <div className="rounded-xl border border-border bg-white p-4">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-font-tertiary">
          <Braces size={12} /> Haz clic para insertar un dato del Excel donde esté tu cursor
        </p>
        <div className="flex flex-wrap gap-1.5">
          {excel.columns.map((c) => (
            <button
              key={c}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertVar(c)}
              className="rounded-full bg-primary-subtle px-2.5 py-1 font-mono text-xs font-semibold text-primary transition hover:bg-primary hover:text-white"
            >
              {'{{'}{c}{'}}'}
            </button>
          ))}
        </div>
        {unknown.length > 0 && (
          <p className="mt-3 flex flex-wrap items-center gap-1.5 text-xs font-semibold text-error">
            <AlertTriangle size={13} /> Hay datos que NO existen en tu Excel (bórralos del correo para continuar):
            {unknown.map((u) => (
              <span key={u} className="rounded-full bg-red-50 px-2 py-0.5 font-mono">{'{{'}{u}{'}}'}</span>
            ))}
          </p>
        )}
      </div>

      {/* Asunto */}
      <div className="rounded-xl border border-border bg-white p-4">
        <label className="mb-1.5 block text-sm font-semibold text-font-primary">Asunto</label>
        <input
          ref={subjectRef}
          value={subject}
          onFocus={() => setLastFocus('subject')}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Asunto del correo"
          className="h-10 w-full rounded-md border border-border px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Editor visual del correo */}
      <div className="rounded-xl border border-border bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {/* Barra de formato */}
          <div className={cn('flex items-center gap-1', mode !== 'editar' && 'pointer-events-none opacity-40')}>
            {TOOLS.map((t) => (
              <button
                key={t.cmd}
                title={t.title}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => exec(t.cmd)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary"
              >
                <t.icon size={14} />
              </button>
            ))}
            <button
              title="Enlace"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const url = window.prompt('URL del enlace (https://...)');
                if (url) exec('createLink', url);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary"
            >
              <Link2 size={14} />
            </button>
            <button
              title="Quitar formato"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('removeFormat')}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary"
            >
              <RemoveFormatting size={14} />
            </button>
            <button
              title="Deshacer"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec('undo')}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-font-secondary transition hover:border-primary hover:text-primary"
            >
              <Undo2 size={14} />
            </button>
          </div>

          {/* Editar / Vista previa */}
          <div className="flex items-center gap-2">
            {mode === 'preview' && (
              <select
                value={previewRow}
                onChange={(e) => setPreviewRow(Number(e.target.value))}
                className="h-8 max-w-[200px] rounded-md border border-border bg-white px-2 text-xs focus:border-primary focus:outline-none"
              >
                {excel.rows.slice(0, 20).map((r, i) => (
                  <option key={i} value={i}>
                    Fila {i + 1}: {r['CORREO']}
                  </option>
                ))}
              </select>
            )}
            <div className="flex rounded-md border border-border p-0.5">
              <button
                onClick={() => setMode('editar')}
                className={cn(
                  'flex h-7 items-center gap-1 rounded px-2.5 text-xs font-semibold transition',
                  mode === 'editar' ? 'bg-primary text-white' : 'text-font-secondary hover:bg-bg',
                )}
              >
                <Pencil size={12} /> Editar
              </button>
              <button
                onClick={() => {
                  syncFromEditor();
                  setMode('preview');
                }}
                className={cn(
                  'flex h-7 items-center gap-1 rounded px-2.5 text-xs font-semibold transition',
                  mode === 'preview' ? 'bg-primary text-white' : 'text-font-secondary hover:bg-bg',
                )}
              >
                <Eye size={12} /> Vista previa
              </button>
            </div>
          </div>
        </div>

        {/* Lienzo del correo */}
        <div className="rounded-xl bg-bg p-3 sm:p-6">
          {mode === 'editar' ? (
            <div className="mx-auto max-w-[600px] overflow-hidden rounded-xl border border-border bg-white shadow-sm">
              {/* Header fijo (no editable) */}
              <div className="relative" style={{ backgroundColor: '#0A1931', padding: '22px 32px' }}>
                <span
                  style={{
                    fontFamily: 'Arial,Helvetica,sans-serif',
                    fontSize: 20,
                    fontWeight: 'bold',
                    color: '#FFFFFF',
                    letterSpacing: 1,
                  }}
                >
                  ISOFT <span style={{ color: '#47B5FF' }}>People</span>
                </span>
                <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-white/50">
                  <Lock size={9} /> fijo
                </span>
              </div>
              <div style={{ height: 4, background: 'linear-gradient(90deg,#0A1931,#1363DA,#47B5FF)' }} />

              {/* Contenido EDITABLE */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={syncFromEditor}
                onFocus={() => setLastFocus('editor')}
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
                onBlur={saveSelection}
                dangerouslySetInnerHTML={{ __html: initialInner.current }}
                className="min-h-[260px] cursor-text outline-none transition focus:bg-primary-subtle/20"
                style={{
                  padding: 32,
                  fontFamily: 'Arial,Helvetica,sans-serif',
                  color: '#0F172A',
                  fontSize: 15,
                  lineHeight: 1.6,
                }}
              />

              {/* Footer fijo (no editable) */}
              <div
                className="relative"
                style={{
                  padding: '18px 32px',
                  backgroundColor: '#F8FAFC',
                  borderTop: '1px solid #E2E8F0',
                  fontFamily: 'Arial,Helvetica,sans-serif',
                  fontSize: 12,
                  color: '#94A3B8',
                }}
              >
                Este es un correo interno de ISOFT. Si tienes dudas escribe a{' '}
                <span style={{ color: '#1363DA' }}>soporte@isoft-ste.com</span>.
                <br />
                ISOFT © 2026 · <span style={{ color: '#1363DA' }}>people.isoft-ste.com</span>
                <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-[9px] font-semibold text-font-tertiary">
                  <Lock size={9} /> fijo
                </span>
              </div>
            </div>
          ) : (
            <iframe
              title="preview"
              sandbox=""
              srcDoc={previewHtml}
              className="mx-auto block h-[480px] w-full max-w-[640px] rounded-xl border border-border bg-white"
            />
          )}
        </div>
        <p className="mt-2 text-center text-[11px] text-font-tertiary">
          {mode === 'editar'
            ? 'Haz clic sobre el texto del correo para editarlo. El encabezado y el pie son fijos de la marca.'
            : 'Así le llegará el correo a cada colaborador, con sus datos reales.'}
        </p>
      </div>

      {/* Prueba + continuar */}
      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <FlaskConical size={15} className="text-primary" />
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="tu-correo@isoft-ste.com"
              className="h-9 w-full max-w-[260px] rounded-md border border-border px-3 text-sm focus:border-primary focus:outline-none"
            />
            <button
              onClick={() => {
                setTestMsg(null);
                sendTest.mutate(
                  {
                    to: testTo.trim(),
                    subject,
                    bodyHtml: buildEmailHtml(bodyInner),
                    columns: excel.columns,
                    sampleRow: row,
                  },
                  {
                    onSuccess: () =>
                      setTestMsg({
                        ok: true,
                        text: `Prueba enviada a ${testTo.trim()}. Si no aparece en la bandeja, revisa la carpeta de spam / correo no deseado.`,
                      }),
                    onError: (e: any) =>
                      setTestMsg({
                        ok: false,
                        text: e?.response?.data?.error?.message ?? 'No se pudo enviar la prueba.',
                      }),
                  },
                );
              }}
              disabled={!EMAIL_RE.test(testTo.trim()) || sendTest.isPending || !subject || !bodyInner}
              className="flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary-subtle px-3 text-sm font-semibold text-primary transition hover:bg-primary hover:text-white disabled:opacity-50"
            >
              {sendTest.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar prueba
            </button>
          </div>
          <button
            onClick={() => {
              syncFromEditor();
              onNext();
            }}
            disabled={!canContinue}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Continuar <ArrowRight size={15} />
          </button>
        </div>
        {testMsg && (
          <div
            className={cn(
              'mt-3 flex items-start gap-2 rounded-lg p-3 text-sm font-medium',
              testMsg.ok ? 'bg-accent-light text-accent' : 'bg-red-50 text-error',
            )}
          >
            {testMsg.ok ? <Check size={15} className="mt-0.5" /> : <X size={15} className="mt-0.5" />}
            {testMsg.text}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================ Paso 4: Enviar ============================ */

function StepSend({
  excel,
  subject,
  bodyInner,
  templateKey,
  onDone,
}: {
  excel: ParsedExcel;
  subject: string;
  bodyInner: string;
  templateKey: string | null;
  onDone: () => void;
}) {
  const createBroadcast = useCreateBroadcast();
  const sendBatch = useSendBroadcastBatch();
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sent, setSent] = useState(0);
  const [failed, setFailed] = useState<Array<{ email: string; error?: string }>>([]);
  const [finished, setFinished] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);

  const total = excel.rows.length;

  const run = async () => {
    setSending(true);
    setFatal(null);
    try {
      const { id } = await createBroadcast.mutateAsync({
        subject,
        templateKey,
        bodyHtml: buildEmailHtml(bodyInner),
        columns: excel.columns,
        totalRecipients: total,
      });
      let sentAcc = 0;
      const failedAcc: Array<{ email: string; error?: string }> = [];
      for (let i = 0; i < total; i += BATCH_SIZE) {
        const rows = excel.rows.slice(i, i + BATCH_SIZE);
        const res = await sendBatch.mutateAsync({ id, rows });
        sentAcc += res.results.filter((r) => r.ok).length;
        failedAcc.push(...res.results.filter((r) => !r.ok));
        setSent(sentAcc);
        setFailed([...failedAcc]);
        setProgress(Math.min(total, i + rows.length));
      }
      setFinished(true);
    } catch (e: any) {
      setFatal(e?.response?.data?.error?.message ?? 'Error al enviar la difusión.');
    } finally {
      setSending(false);
    }
  };

  const pct = total ? Math.round((progress / total) * 100) : 0;

  return (
    <div className="mx-auto w-full max-w-2xl rounded-xl border border-border bg-white p-6">
      {!sending && !finished && (
        <>
          <h3 className="mb-4 text-base font-bold text-font-primary">Confirmar difusión</h3>
          <div className="space-y-2 rounded-lg bg-bg p-4 text-sm">
            <p>
              <span className="text-font-secondary">Destinatarios:</span>{' '}
              <strong>{total} colaboradores</strong> (columna CORREO de {excel.fileName})
            </p>
            <p>
              <span className="text-font-secondary">Asunto:</span> <strong>{subject}</strong>
            </p>
            <p>
              <span className="text-font-secondary">Remitente:</span> soporte@isoft-ste.com
            </p>
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-font-tertiary">
            <AlertTriangle size={13} className="text-warning" /> El envío no se puede deshacer.
            Verifica la vista previa y la prueba antes de continuar.
          </p>
          {fatal && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-error">{fatal}</p>}
          <button
            onClick={run}
            className="mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-bold text-white shadow-sm transition hover:bg-primary-dark"
          >
            <Send size={16} /> Enviar a {total} destinatarios
          </button>
        </>
      )}

      {(sending || finished) && (
        <>
          <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-font-primary">
            {finished ? (
              <Check size={18} className="text-accent" />
            ) : (
              <Loader2 size={18} className="animate-spin text-primary" />
            )}
            {finished ? 'Difusión completada' : 'Enviando…'}
          </h3>
          <div className="h-3 w-full overflow-hidden rounded-full bg-bg">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <Badge variant="success">{sent} enviados</Badge>
            {failed.length > 0 && <Badge variant="error">{failed.length} fallidos</Badge>}
            <span className="text-xs text-font-tertiary">
              {progress}/{total} procesados ({pct}%)
            </span>
          </div>
          {failed.length > 0 && (
            <div className="mt-3 max-h-44 overflow-y-auto rounded-lg border border-border-light">
              {failed.map((f, i) => (
                <div key={i} className="flex items-center gap-2 border-b border-border-light px-3 py-1.5 text-xs last:border-0">
                  <X size={11} className="flex-shrink-0 text-error" />
                  <span className="font-medium">{f.email}</span>
                  <span className="truncate text-font-tertiary">{f.error}</span>
                </div>
              ))}
            </div>
          )}
          {finished && (
            <button
              onClick={onDone}
              className="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-md border border-border text-sm font-semibold text-font-primary transition hover:bg-bg"
            >
              Nueva difusión
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ============================ Historial ============================ */

function BroadcastHistory() {
  const { data } = useBroadcasts();
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-font-primary">
        <History size={15} className="text-primary" /> Historial de difusiones
      </h3>
      <div className="flex flex-col">
        {items.map((b: BroadcastListItem) => (
          <div
            key={b.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-light py-2.5 text-sm last:border-0"
          >
            <span className="min-w-0 flex-1 truncate font-semibold text-font-primary">{b.subject}</span>
            <span className="text-xs text-font-tertiary">
              {new Date(b.createdAt).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
            <Badge variant="success">{b.sentCount}/{b.totalRecipients}</Badge>
            {b.failedCount > 0 && <Badge variant="error">{b.failedCount} fallidos</Badge>}
            {b.status === 'SENDING' && <Badge variant="warning" dot>En curso</Badge>}
          </div>
        ))}
      </div>
    </div>
  );
}
